import * as React from "react";
import SchedulesList from "./SchedulesList";
import { useState, useEffect, useCallback } from "react";
import ScheduleEditor from "./ScheduleEditor";
import daysOfWeek from "./helpers/daysOfWeek";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryGrayButton,
  Severity
} from "@velaro/velaro-component-lib";
import { SchedulingModel, WorkSchedule } from "./types";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { set } from "lodash";

export default function ScheduleManager() {
  const [selectedScheduleIndex, setSelectedScheduleIndex] = useState<number>();
  const [schedulingModel, setSchedulingModel] = useState<SchedulingModel>({
    schedules: []
  } as any);
  const [loading, setLoading] = useState<boolean>(true);
  const [pendingChanges, setPendingChanges] = useState<boolean>(false);
  const [newScheduleModal, setNewScheduleModal] = useState<boolean>(false);
  const [showConfirmRemoveModal, setShowConfirmRemoveModal] = useState<boolean>(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<WorkSchedule | null>(null);

  const api = useApi();
  const { displayToast } = useToastAlert();

  function onAddSchedule() {
    const newSchedule: any = {
      name: "New schedule",
      enabled: true,
      shifts: [
        {
          startDayOfWeek: daysOfWeek.Monday,
          startHour: 9,
          startMinute: 0,
          endDayOfWeek: daysOfWeek.Monday,
          endHour: 17,
          endMinute: 0
        }
      ],
      teams: []
    };
    setNewScheduleModal(true);
    schedulingModel.schedules.push(newSchedule);
    setSelectedScheduleIndex(schedulingModel.schedules.indexOf(newSchedule));
    onSchedulingModelUpdate();
  }

  function onSelectSchedule(selectedIndex: number) {
    setSelectedScheduleIndex(selectedIndex);
  }

  function onDeleteSchedule(schedule: WorkSchedule) {
    setScheduleToDelete(schedule);
    setShowConfirmRemoveModal(true);
  }

  function confirmDeleteSchedule() {
    if (scheduleToDelete) {
      setSelectedScheduleIndex(undefined);
      const index = schedulingModel.schedules.indexOf(scheduleToDelete);
      schedulingModel.schedules.splice(index, 1);
      setNewScheduleModal(false);
      onSchedulingModelUpdate();
      setShowConfirmRemoveModal(false);
      setScheduleToDelete(null);
      displayToast(Severity.Success, "Schedule deleted successfully");
    }
  }

  function onToggleScheduleEnabled(selectedIndex: number) {
    const schedule = schedulingModel.schedules[selectedIndex];
    schedule.enabled = !schedule.enabled;
    onSchedulingModelUpdate();
  }

  function onConfirmScheduleChanges(newSchedule: WorkSchedule) {
    // todo: this looks pretty wrong.
    setSelectedScheduleIndex(undefined);
    setNewScheduleModal(false);
    schedulingModel.schedules[selectedScheduleIndex!] = newSchedule;
    onSchedulingModelUpdate();
  }

  function onToggleEnableScheduling() {
    schedulingModel.enableScheduling = !schedulingModel.enableScheduling;
    onSchedulingModelUpdate();
  }

  function onToggleUnscheduledTeamsAlwaysUnavailable() {
    schedulingModel.unscheduledTeamsAlwaysUnavailable =
      !schedulingModel.unscheduledTeamsAlwaysUnavailable;
    onSchedulingModelUpdate();
  }

  function onSchedulingModelUpdate() {
    setSchedulingModel({ ...schedulingModel });
    setPendingChanges(true);
  }

  const fetchSchedules = useCallback(async () => {
    const resp = await api.messaging.get("WorkSchedules/List");
    const model = await resp.json();
    setSchedulingModel(model);
    setLoading(false);
  }, [api.messaging]);

  async function onSave() {
    setPendingChanges(false);
    const resp = await api.messaging.post("WorkSchedules", schedulingModel);
    displayToast(Severity.Success, "Schedules saved successfully");
  }

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  function renderSaveButton() {
    if (!pendingChanges) {
      return;
    }

    return <PrimaryBlueButton onClick={onSave} label="Save all" />;
  }

  function renderEditorModal() {
    if (selectedScheduleIndex === undefined) {
      return;
    }

    return (
      <Modal show maxWidth="max-w-5xl">
        <ScheduleEditor
          schedule={schedulingModel.schedules[selectedScheduleIndex]}
          onConfirm={onConfirmScheduleChanges}
          onReturn={() => {
            setSelectedScheduleIndex(undefined);
          }}
          isNewSchedule={newScheduleModal}
        />
      </Modal>
    );
  }

  function renderConfirmRemoveModal() {
    return (
      <Modal show={showConfirmRemoveModal}>
        <ModalHeader
          title="Confirm Remove Schedule"
          onClose={() => setShowConfirmRemoveModal(false)}
        />
        <ModalBody>
          <div className="m-4">
            Are you sure you want to remove this schedule?
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="mx-4 my-2 flex justify-between items-center">
            <SecondaryGrayButton
              label={"Cancel"}
              onClick={() => setShowConfirmRemoveModal(false)}
            />
            <PrimaryBlueButton
              label={"Remove"}
              onClick={confirmDeleteSchedule}
            />
          </div>
        </ModalFooter>
      </Modal>
    );
  }

  function renderScheduleList() {
    if (!schedulingModel.schedules.length) {
      return <div>Please add new schedule</div>;
    }

    return (
      <SchedulesList
        schedules={schedulingModel.schedules}
        onSelectSchedule={onSelectSchedule}
        onToggleScheduleEnabled={onToggleScheduleEnabled}
        onDeleteSchedule={onDeleteSchedule}
      />
    );
  }

  function renderContent() {
    if (loading) {
      return <div>Loading</div>;
    }

    return (
      <React.Fragment>
        <div className="mb-4">{renderScheduleList()}</div>
        <div>{renderSaveButton()}</div>
        {renderEditorModal()}
        {renderConfirmRemoveModal()}
      </React.Fragment>
    );
  }

  return (
    <>
      <div className="ml-auto">
        <div className="pb-4">
          <PrimaryBlueButton onClick={onAddSchedule} label="Add schedule" />
        </div>
      </div>
      <div className="m-4 pb-12">
        <div className="work-schedules">{renderContent()}</div>
      </div>
    </>
  );
}
